import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Play, CheckCircle2, XCircle, ArrowRight, ExternalLink, Award, Sparkles,
  Lightbulb, RotateCcw, FileText, Shield, Lock, Briefcase, Scale,
} from "lucide-react";

/* ============================================================================
 * Training Samples — interactive demo for the Giggle proposal.
 *
 * Five real CompliSun training videos with one interaction type per video,
 * to show off the platform's full assessment toolkit in one section:
 *   - True / False
 *   - Multiple choice
 *   - Fill in the blank
 *   - Interactive matching
 *
 * We track three signals per lesson:
 *   • video_started (first play event)
 *   • interaction_attempted (any answer submitted)
 *   • interaction_correct (the assessment passed)
 *
 * In production this fires through the same per-module attestation pipeline
 * that backs the examiner-ready reporting story in the rest of the proposal.
 * Here it stays in client memory and renders a "Knowledge check complete" pill.
 * ========================================================================== */

const NAVY = "#1A2B5E";
const CYAN = "#40CFDE";
const ORANGE = "#FF801D";

const LESSONS = [
  {
    id: "conflicts",
    title: "Conflicts of Interest",
    track: "Workplace · All-Staff",
    icon: Briefcase,
    videoUrl: "https://customer-assets.emergentagent.com/job_complisun-pitch/artifacts/7qshnu23_Conflicts.mp4",
    blurb: "Recognising conflict scenarios before they become disclosure violations.",
    interaction: {
      type: "true-false",
      prompt: "A conflict of interest only exists when money or gifts change hands between an employee and a counterparty.",
      answer: false,
      rationale: "Conflicts of interest also arise from relationships, outside roles, and decision-making influence — no payment required. The duty is to disclose and recuse.",
    },
  },
  {
    id: "fin-laws",
    title: "Intro to Financial Services Laws",
    track: "Federal · Track A & B",
    icon: Scale,
    videoUrl: "https://customer-assets.emergentagent.com/job_complisun-pitch/artifacts/h6y8yoqx_Intro%20Financial%20Servcies%20Laws.mp4",
    blurb: "The federal lending statutes every customer-facing team needs to recognise.",
    interaction: {
      type: "multiple-choice",
      prompt: "Which federal statute governs the disclosure of finance charges and APR to consumer borrowers?",
      options: [
        { id: "a", label: "ECOA (Equal Credit Opportunity Act)" },
        { id: "b", label: "TILA (Truth in Lending Act) · Regulation Z" },
        { id: "c", label: "FCRA (Fair Credit Reporting Act)" },
        { id: "d", label: "GLBA (Gramm-Leach-Bliley Act)" },
      ],
      answer: "b",
      rationale: "TILA — implemented through Regulation Z — requires clear, uniform disclosure of credit terms so borrowers can compare offers.",
    },
  },
  {
    id: "cms",
    title: "Compliance Management System",
    track: "All-Staff · Foundations",
    icon: Shield,
    videoUrl: "https://customer-assets.emergentagent.com/job_complisun-pitch/artifacts/n2wr9igp_Intro%20Compliance%20Management%20System.mp4",
    blurb: "What a regulator means when they ask 'show me your CMS'.",
    interaction: {
      type: "fill-blank",
      prompt: "Examiners typically expect a CMS to demonstrate four elements — board and management oversight, a compliance program, response to consumer ____, and independent compliance audit.",
      accepted: ["complaints", "complaint"],
      rationale: "The fourth pillar examiners cite is the consumer-complaint response process — intake, triage, resolution and root-cause analysis.",
    },
  },
  {
    id: "privacy",
    title: "Data Protection",
    track: "Privacy · All-Staff",
    icon: Lock,
    videoUrl: "https://customer-assets.emergentagent.com/job_complisun-pitch/artifacts/1jdgjkgi_Intro%20to%20Data%20Protection.mp4",
    blurb: "GLBA, CCPA, GDPR, FCRA — which law covers which scope.",
    interaction: {
      type: "match",
      prompt: "Match the regulation to the scope it governs.",
      left: [
        { id: "glba", label: "GLBA" },
        { id: "ccpa", label: "CCPA / CPRA" },
        { id: "gdpr", label: "GDPR" },
        { id: "fcra", label: "FCRA" },
      ],
      right: [
        { id: "fin",  label: "Non-public personal info held by US financial institutions" },
        { id: "cal",  label: "California-resident consumer privacy rights" },
        { id: "eu",   label: "Personal data of individuals in the EU / EEA" },
        { id: "rep",  label: "Consumer reporting agencies & permissible-purpose use" },
      ],
      answer: { glba: "fin", ccpa: "cal", gdpr: "eu", fcra: "rep" },
      rationale: "GLBA → US FI customer NPI · CCPA/CPRA → California residents · GDPR → EU/EEA data subjects · FCRA → CRAs and permissible-purpose rules.",
    },
  },
  {
    id: "aml",
    title: "Anti-Money Laundering · Intro",
    track: "BSA/AML · All-Staff",
    icon: FileText,
    videoUrl: "https://customer-assets.emergentagent.com/job_complisun-pitch/artifacts/dkgmk7mh_AML%20Intro.mp4",
    blurb: "Spot the three textbook AML patterns: placement, layering, integration.",
    interaction: {
      type: "multiple-choice",
      prompt: "An employee notices a borrower depositing nine $9,500 cash payments across multiple branches in a single week. Which AML technique is this most consistent with?",
      options: [
        { id: "a", label: "Layering" },
        { id: "b", label: "Smurfing / structuring (placement)" },
        { id: "c", label: "Integration" },
        { id: "d", label: "Reverse money laundering" },
      ],
      answer: "b",
      rationale: "Structuring — deliberately breaking cash into amounts under the $10,000 reporting threshold across people or branches — is a textbook placement-stage red flag and a SAR trigger on its own.",
    },
  },
];

export default function TrainingSamples({ trainingRequestUrl }) {
  const [activeId, setActiveId] = useState(LESSONS[0].id);
  const [completed, setCompleted] = useState(() => new Set());     // lesson IDs with interaction passed
  const [started, setStarted] = useState(() => new Set());          // lesson IDs where video play was triggered

  const active = useMemo(() => LESSONS.find((l) => l.id === activeId), [activeId]);
  const totalCompleted = completed.size;
  const totalStarted = started.size;

  const markStarted = (id) => {
    setStarted((prev) => {
      if (prev.has(id)) return prev;
      const n = new Set(prev); n.add(id); return n;
    });
  };
  const markCompleted = (id) => {
    setCompleted((prev) => {
      if (prev.has(id)) return prev;
      const n = new Set(prev); n.add(id);
      return n;
    });
  };

  return (
    <section id="samples" className="anchor-target py-16 md:py-20">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-3">
        <div>
          <div className="gg-eyebrow mb-3 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Sample lessons · live interactivity
          </div>
          <h2 className="gg-h text-3xl md:text-5xl leading-tight max-w-3xl" style={{ color: NAVY }}>
            Try the assessments. <span style={{ color: ORANGE }}>Right here, on this page.</span>
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="samples-progress">
          <span className="gg-chip">
            {totalStarted}/{LESSONS.length} videos started
          </span>
          <span
            className={"gg-chip-orange"}
            style={{
              background: totalCompleted === LESSONS.length ? ORANGE : "transparent",
              color: totalCompleted === LESSONS.length ? "#fff" : "#B85700",
              borderColor: ORANGE,
            }}
          >
            <CheckCircle2 className="inline h-3 w-3 mr-1" />
            {totalCompleted}/{LESSONS.length} knowledge checks passed
          </span>
        </div>
      </div>
      <p className="mt-2 text-base md:text-lg max-w-3xl leading-relaxed" style={{ color: NAVY, opacity: 0.78 }}>
        Five real CompliSun lessons — one for each major track. Every video here is paired with a
        different interaction type so you can see the platform's full assessment toolkit at work:
        <strong> True/False, Multiple choice, Fill-in-the-blank, and Interactive matching</strong>.
        Hit play on any lesson and answer the check below.
      </p>

      <div className="mt-8 grid grid-cols-12 gap-5">
        {/* Left rail — lesson picker */}
        <aside className="col-span-12 md:col-span-4 flex flex-col gap-2" data-testid="samples-rail">
          {LESSONS.map((l) => {
            const on = l.id === activeId;
            const wasStarted = started.has(l.id);
            const wasDone = completed.has(l.id);
            const Ic = l.icon;
            return (
              <button
                type="button"
                key={l.id}
                onClick={() => setActiveId(l.id)}
                data-testid={`samples-pick-${l.id}`}
                aria-pressed={on}
                className="text-left rounded-xl p-3.5 transition-all flex items-start gap-3"
                style={{
                  background: on ? "#fff" : `${NAVY}06`,
                  border: `1.5px solid ${on ? CYAN : `${NAVY}12`}`,
                  boxShadow: on ? `0 8px 24px -14px ${CYAN}99` : "none",
                }}
              >
                <div
                  className="h-9 w-9 rounded-md flex items-center justify-center flex-shrink-0"
                  style={{ background: on ? `${CYAN}22` : `${NAVY}10`, color: on ? CYAN : NAVY }}
                >
                  <Ic className="h-4 w-4" strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="gg-h text-[14px] leading-snug" style={{ color: NAVY }}>{l.title}</div>
                  <div className="mono text-[9px] uppercase tracking-[0.18em] mt-0.5" style={{ color: NAVY, opacity: 0.55 }}>
                    {l.track} · {labelForType(l.interaction.type)}
                  </div>
                </div>
                <div className="flex flex-col gap-1 items-end flex-shrink-0">
                  {wasStarted && (
                    <span
                      className="text-[9px] font-bold uppercase tracking-[0.16em] rounded-full px-1.5 py-0.5"
                      style={{ background: `${CYAN}22`, color: NAVY }}
                      data-testid={`samples-started-${l.id}`}
                    >
                      Watched
                    </span>
                  )}
                  {wasDone && (
                    <span
                      className="text-[9px] font-bold uppercase tracking-[0.16em] rounded-full px-1.5 py-0.5"
                      style={{ background: ORANGE, color: "#fff" }}
                      data-testid={`samples-done-${l.id}`}
                    >
                      Passed
                    </span>
                  )}
                </div>
              </button>
            );
          })}

          {/* Request-training link */}
          <a
            href={trainingRequestUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="samples-request-training"
            className="mt-3 rounded-xl p-4 flex items-center gap-3 transition-colors"
            style={{
              background: `${ORANGE}10`,
              border: `1.5px dashed ${ORANGE}66`,
              color: NAVY,
              textDecoration: "none",
            }}
          >
            <div className="h-9 w-9 rounded-md grid place-items-center flex-shrink-0" style={{ background: `${ORANGE}22`, color: ORANGE }}>
              <Lightbulb className="h-4 w-4" strokeWidth={1.6} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="gg-h text-[14px] leading-tight">Need a custom topic?</div>
              <div className="text-[11px] mt-0.5" style={{ color: NAVY, opacity: 0.7 }}>
                Use the standard CompliSun request form ↗
              </div>
            </div>
            <ExternalLink className="h-4 w-4 flex-shrink-0" style={{ color: ORANGE }} />
          </a>
        </aside>

        {/* Right pane — active lesson */}
        <div className="col-span-12 md:col-span-8" data-testid="samples-pane">
          <LessonPlayer
            lesson={active}
            onStart={() => markStarted(active.id)}
            onPass={() => markCompleted(active.id)}
            wasCompleted={completed.has(active.id)}
            key={active.id}
          />
        </div>
      </div>
    </section>
  );
}

function labelForType(type) {
  if (type === "true-false") return "True / False";
  if (type === "multiple-choice") return "Multiple choice";
  if (type === "fill-blank") return "Fill in the blank";
  if (type === "match") return "Interactive matching";
  return type;
}

/* --------------------- LESSON PLAYER -------------------------------------- */
function LessonPlayer({ lesson, onStart, onPass, wasCompleted }) {
  const videoRef = useRef(null);
  const [playStarted, setPlayStarted] = useState(false);

  const handlePlay = () => {
    if (playStarted) return;
    setPlayStarted(true);
    onStart?.();
    toast.success(`Tracked: started "${lesson.title}"`);
  };

  return (
    <div className="space-y-4" data-testid={`lesson-${lesson.id}`}>
      <article className="gg-card overflow-hidden">
        <div
          className="aspect-video bg-black relative"
          data-testid="lesson-video-wrap"
        >
          <video
            ref={videoRef}
            src={lesson.videoUrl}
            controls
            preload="metadata"
            onPlay={handlePlay}
            className="w-full h-full"
            data-testid="lesson-video"
          />
          {!playStarted && (
            <div
              className="absolute inset-0 pointer-events-none flex items-end justify-between p-4"
              style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.55) 100%)" }}
            >
              <span
                className="text-[10px] uppercase tracking-[0.22em] font-bold px-2 py-1 rounded"
                style={{ background: "rgba(255,255,255,0.15)", color: "#fff", backdropFilter: "blur(4px)" }}
              >
                Sample lesson · CompliSun
              </span>
              <span
                className="text-[10px] uppercase tracking-[0.22em] font-bold inline-flex items-center gap-1.5 px-2.5 py-1 rounded"
                style={{ background: "#FF801D", color: "#fff" }}
              >
                <Play className="h-3 w-3" /> Press play to begin
              </span>
            </div>
          )}
        </div>
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="mono text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: CYAN }}>
                {lesson.track} · {labelForType(lesson.interaction.type)}
              </div>
              <h3 className="gg-h text-2xl mt-0.5 leading-tight" style={{ color: NAVY }}>{lesson.title}</h3>
              <p className="text-[14px] mt-1.5 leading-relaxed" style={{ color: NAVY, opacity: 0.78 }}>
                {lesson.blurb}
              </p>
            </div>
            {wasCompleted && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em]"
                style={{ background: `${ORANGE}18`, color: ORANGE, border: `1px solid ${ORANGE}66` }}
                data-testid="lesson-complete-pill"
              >
                <Award className="h-3 w-3" /> Knowledge check complete
              </span>
            )}
          </div>
        </div>
      </article>

      <Interaction lesson={lesson} onPass={onPass} />
    </div>
  );
}

/* --------------------- INTERACTION DISPATCH ------------------------------- */
function Interaction({ lesson, onPass }) {
  const t = lesson.interaction.type;
  if (t === "true-false")       return <TrueFalse  lesson={lesson} onPass={onPass} />;
  if (t === "multiple-choice")  return <MultiChoice lesson={lesson} onPass={onPass} />;
  if (t === "fill-blank")       return <FillBlank   lesson={lesson} onPass={onPass} />;
  if (t === "match")            return <Matching    lesson={lesson} onPass={onPass} />;
  return null;
}

/* ----- helpers ------------------------------------------------------------ */
function InteractionShell({ children, "data-testid": testId }) {
  return (
    <article className="gg-card p-5 md:p-6" data-testid={testId}>
      {children}
    </article>
  );
}

function Verdict({ ok, rationale }) {
  return (
    <div
      className="mt-4 rounded-md p-3 flex items-start gap-2.5"
      style={{
        background: ok ? "rgba(16,185,129,0.10)" : "rgba(244,63,94,0.08)",
        border: `1px solid ${ok ? "rgba(16,185,129,0.45)" : "rgba(244,63,94,0.45)"}`,
      }}
      data-testid={ok ? "verdict-correct" : "verdict-incorrect"}
    >
      {ok ? (
        <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: "#10B981" }} />
      ) : (
        <XCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: "#F43F5E" }} />
      )}
      <div className="text-[13px] leading-relaxed" style={{ color: NAVY }}>
        <strong>{ok ? "Correct." : "Not quite."}</strong> {rationale}
      </div>
    </div>
  );
}

function Heading({ lesson }) {
  return (
    <>
      <div className="gg-eyebrow" style={{ color: ORANGE }}>Knowledge check · {labelForType(lesson.interaction.type)}</div>
      <p className="gg-h text-lg md:text-xl mt-1 leading-snug" style={{ color: NAVY }}>{lesson.interaction.prompt}</p>
    </>
  );
}

/* --------------------- 1) TRUE / FALSE ------------------------------------ */
function TrueFalse({ lesson, onPass }) {
  const [pick, setPick] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const correctVal = lesson.interaction.answer;

  const submit = () => {
    if (pick === null) return;
    setSubmitted(true);
    if (pick === correctVal) onPass?.();
  };
  const reset = () => { setPick(null); setSubmitted(false); };
  const ok = submitted && pick === correctVal;

  return (
    <InteractionShell data-testid="interaction-true-false">
      <Heading lesson={lesson} />
      <div className="mt-4 grid grid-cols-2 gap-3">
        {[
          { val: true,  label: "True"  },
          { val: false, label: "False" },
        ].map((opt) => {
          const on = pick === opt.val;
          const isCorrectChoice = submitted && opt.val === correctVal;
          const isWrongChoice = submitted && on && opt.val !== correctVal;
          return (
            <button
              type="button"
              key={String(opt.val)}
              onClick={() => !submitted && setPick(opt.val)}
              data-testid={`tf-${opt.label.toLowerCase()}`}
              disabled={submitted}
              className="rounded-md py-4 text-base font-bold transition-all"
              style={{
                background: choiceBg(isCorrectChoice, isWrongChoice, on, "1a"),
                border: `2px solid ${choiceBorder(isCorrectChoice, isWrongChoice, on)}`,
                color: NAVY,
                cursor: submitted ? "default" : "pointer",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {!submitted ? (
        <button
          type="button"
          onClick={submit}
          disabled={pick === null}
          data-testid="tf-submit"
          className="gg-cta mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-md text-sm font-bold disabled:opacity-40"
        >
          Submit answer <ArrowRight className="h-4 w-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={reset}
          data-testid="tf-reset"
          className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-md text-sm font-bold"
          style={{ background: "#fff", color: NAVY, border: `1.5px solid ${NAVY}25` }}
        >
          <RotateCcw className="h-3.5 w-3.5" /> Try again
        </button>
      )}
      {submitted && <Verdict ok={ok} rationale={lesson.interaction.rationale} />}
    </InteractionShell>
  );
}

/* --------------------- 2) MULTIPLE CHOICE --------------------------------- */
function MultiChoice({ lesson, onPass }) {
  const [pick, setPick] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const correctId = lesson.interaction.answer;

  const submit = () => {
    if (!pick) return;
    setSubmitted(true);
    if (pick === correctId) onPass?.();
  };
  const reset = () => { setPick(null); setSubmitted(false); };
  const ok = submitted && pick === correctId;

  return (
    <InteractionShell data-testid="interaction-multi-choice">
      <Heading lesson={lesson} />
      <ul className="mt-4 space-y-2">
        {lesson.interaction.options.map((opt) => {
          const on = pick === opt.id;
          const isRight = submitted && opt.id === correctId;
          const isWrongPick = submitted && on && opt.id !== correctId;
          return (
            <li key={opt.id}>
              <button
                type="button"
                onClick={() => !submitted && setPick(opt.id)}
                disabled={submitted}
                data-testid={`mc-opt-${opt.id}`}
                className="w-full text-left rounded-md px-4 py-3 flex items-center gap-3 transition-all"
                style={{
                  background: isRight ? "rgba(16,185,129,0.10)"
                    : isWrongPick ? "rgba(244,63,94,0.08)"
                    : on ? `${CYAN}10` : "#fff",
                  border: `1.5px solid ${
                    isRight ? "#10B981"
                    : isWrongPick ? "#F43F5E"
                    : on ? CYAN : `${NAVY}18`
                  }`,
                  color: NAVY,
                  cursor: submitted ? "default" : "pointer",
                }}
              >
                <span
                  className="h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 mono text-[11px] font-bold"
                  style={{
                    background: on ? CYAN : `${NAVY}10`,
                    color: on ? "#fff" : NAVY,
                  }}
                >
                  {opt.id.toUpperCase()}
                </span>
                <span className="text-[14px]">{opt.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {!submitted ? (
        <button
          type="button"
          onClick={submit}
          disabled={!pick}
          data-testid="mc-submit"
          className="gg-cta mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-md text-sm font-bold disabled:opacity-40"
        >
          Submit answer <ArrowRight className="h-4 w-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={reset}
          data-testid="mc-reset"
          className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-md text-sm font-bold"
          style={{ background: "#fff", color: NAVY, border: `1.5px solid ${NAVY}25` }}
        >
          <RotateCcw className="h-3.5 w-3.5" /> Try again
        </button>
      )}
      {submitted && <Verdict ok={ok} rationale={lesson.interaction.rationale} />}
    </InteractionShell>
  );
}

/* --------------------- 3) FILL IN THE BLANK ------------------------------- */
function FillBlank({ lesson, onPass }) {
  const [value, setValue] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const accepted = (lesson.interaction.accepted || []).map((s) => s.toLowerCase());

  const ok = submitted && accepted.includes(value.trim().toLowerCase());
  const submit = (e) => {
    e?.preventDefault?.();
    if (!value.trim()) return;
    setSubmitted(true);
    if (accepted.includes(value.trim().toLowerCase())) onPass?.();
  };
  const reset = () => { setValue(""); setSubmitted(false); };

  const parts = lesson.interaction.prompt.split("____");

  return (
    <InteractionShell data-testid="interaction-fill-blank">
      <div className="gg-eyebrow" style={{ color: ORANGE }}>Knowledge check · {labelForType(lesson.interaction.type)}</div>
      <form className="mt-2" onSubmit={submit}>
        <p className="gg-h text-lg md:text-xl leading-relaxed" style={{ color: NAVY }}>
          {parts[0]}
          <input
            type="text"
            value={value}
            onChange={(e) => !submitted && setValue(e.target.value)}
            disabled={submitted}
            data-testid="fb-input"
            placeholder="type the missing word…"
            className="inline-block mx-1 px-2.5 py-1 rounded font-medium"
            style={{
              border: `2px dashed ${submitted ? (ok ? "#10B981" : "#F43F5E") : `${NAVY}40`}`,
              background: submitted ? (ok ? "rgba(16,185,129,0.08)" : "rgba(244,63,94,0.06)") : "#fff",
              minWidth: 180,
              color: NAVY,
              fontFamily: "inherit",
              fontSize: "inherit",
            }}
          />
          {parts[1]}
        </p>
        {!submitted ? (
          <button
            type="submit"
            disabled={!value.trim()}
            data-testid="fb-submit"
            className="gg-cta mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-md text-sm font-bold disabled:opacity-40"
          >
            Submit answer <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={reset}
            data-testid="fb-reset"
            className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-md text-sm font-bold"
            style={{ background: "#fff", color: NAVY, border: `1.5px solid ${NAVY}25` }}
          >
            <RotateCcw className="h-3.5 w-3.5" /> Try again
          </button>
        )}
      </form>
      {submitted && <Verdict ok={ok} rationale={lesson.interaction.rationale} />}
    </InteractionShell>
  );
}

/* --------------------- 4) INTERACTIVE MATCHING ---------------------------- */
function Matching({ lesson, onPass }) {
  const { left, right, answer, rationale, prompt } = lesson.interaction;
  const [selectedLeft, setSelectedLeft] = useState(null);
  // map of leftId -> rightId
  const [pairs, setPairs] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const clickLeft = (id) => {
    if (submitted) return;
    setSelectedLeft((cur) => (cur === id ? null : id));
  };
  const clickRight = (rid) => {
    if (submitted || !selectedLeft) return;
    setPairs((prev) => {
      const next = { ...prev };
      // remove any existing pairing for this right
      Object.keys(next).forEach((lid) => { if (next[lid] === rid) delete next[lid]; });
      next[selectedLeft] = rid;
      return next;
    });
    setSelectedLeft(null);
  };
  const clearPair = (lid) => {
    if (submitted) return;
    setPairs((prev) => {
      const n = { ...prev }; delete n[lid]; return n;
    });
  };

  const allPaired = Object.keys(pairs).length === left.length;
  const correctCount = Object.entries(pairs).filter(([lid, rid]) => answer[lid] === rid).length;
  const ok = submitted && correctCount === left.length;

  const submit = () => {
    setSubmitted(true);
    if (correctCount === left.length) onPass?.();
  };
  const reset = () => { setPairs({}); setSelectedLeft(null); setSubmitted(false); };

  const rightUsed = new Set(Object.values(pairs));
  const labelForRight = (rid) => right.find((r) => r.id === rid)?.label || rid;

  const rowStatus = (lid) => {
    if (!submitted) return "none";
    if (pairs[lid] === answer[lid]) return "ok";
    return "bad";
  };

  return (
    <InteractionShell data-testid="interaction-match">
      <div className="gg-eyebrow" style={{ color: ORANGE }}>Knowledge check · {labelForType(lesson.interaction.type)}</div>
      <p className="gg-h text-lg md:text-xl mt-1" style={{ color: NAVY }}>{prompt}</p>

      <div className="mt-1 text-[12px]" style={{ color: NAVY, opacity: 0.6 }}>
        Click a regulation on the left, then click the matching scope on the right.
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* LEFT — anchor items */}
        <ul className="space-y-2" data-testid="match-left">
          {left.map((l) => {
            const matchRid = pairs[l.id];
            const selected = selectedLeft === l.id;
            const status = rowStatus(l.id);
            return (
              <li key={l.id}>
                <button
                  type="button"
                  onClick={() => clickLeft(l.id)}
                  disabled={submitted}
                  data-testid={`match-left-${l.id}`}
                  className="w-full text-left rounded-md px-3.5 py-3 transition-all flex items-center justify-between gap-2"
                  style={{
                    background: status === "ok" ? "rgba(16,185,129,0.10)"
                      : status === "bad" ? "rgba(244,63,94,0.08)"
                      : selected ? `${CYAN}1a` : "#fff",
                    border: `1.5px solid ${
                      status === "ok" ? "#10B981"
                      : status === "bad" ? "#F43F5E"
                      : selected ? CYAN : `${NAVY}18`
                    }`,
                    color: NAVY,
                  }}
                >
                  <span className="gg-h text-[15px]">{l.label}</span>
                  {matchRid && (
                    <span className="flex items-center gap-1.5">
                      <span
                        className="text-[10px] uppercase font-bold tracking-[0.14em] rounded-full px-2 py-0.5"
                        style={{ background: `${CYAN}22`, color: NAVY }}
                      >
                        → {labelForRight(matchRid).slice(0, 26)}{labelForRight(matchRid).length > 26 ? "…" : ""}
                      </span>
                      {!submitted && (
                        <span
                          role="button"
                          aria-label="Clear pair"
                          onClick={(e) => { e.stopPropagation(); clearPair(l.id); }}
                          className="text-[10px] font-bold"
                          style={{ color: NAVY, opacity: 0.55 }}
                        >
                          ×
                        </span>
                      )}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        {/* RIGHT — scope items */}
        <ul className="space-y-2" data-testid="match-right">
          {right.map((r) => {
            const used = rightUsed.has(r.id);
            const ownerLid = Object.keys(pairs).find((lid) => pairs[lid] === r.id);
            const status = submitted && ownerLid
              ? (answer[ownerLid] === r.id ? "ok" : "bad")
              : "none";
            return (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => clickRight(r.id)}
                  disabled={submitted || !selectedLeft}
                  data-testid={`match-right-${r.id}`}
                  className="w-full text-left rounded-md px-3.5 py-3 transition-all flex items-start gap-2"
                  style={{
                    background: status === "ok" ? "rgba(16,185,129,0.10)"
                      : status === "bad" ? "rgba(244,63,94,0.08)"
                      : used ? `${NAVY}08`
                      : "#fff",
                    border: `1.5px solid ${
                      status === "ok" ? "#10B981"
                      : status === "bad" ? "#F43F5E"
                      : used ? `${NAVY}25`
                      : selectedLeft ? `${CYAN}88` : `${NAVY}18`
                    }`,
                    color: NAVY,
                    cursor: submitted ? "default" : selectedLeft ? "pointer" : "not-allowed",
                    opacity: !submitted && !selectedLeft && !used ? 0.85 : 1,
                  }}
                >
                  <span className="text-[14px] leading-relaxed">{r.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {!submitted ? (
        <button
          type="button"
          onClick={submit}
          disabled={!allPaired}
          data-testid="match-submit"
          className="gg-cta mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-md text-sm font-bold disabled:opacity-40"
        >
          Submit matches <ArrowRight className="h-4 w-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={reset}
          data-testid="match-reset"
          className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-md text-sm font-bold"
          style={{ background: "#fff", color: NAVY, border: `1.5px solid ${NAVY}25` }}
        >
          <RotateCcw className="h-3.5 w-3.5" /> Try again
        </button>
      )}

      {submitted && (
        <>
          <div className="mt-3 text-[12px]" style={{ color: NAVY, opacity: 0.7 }} data-testid="match-score">
            <strong>{correctCount} of {left.length}</strong> correct.
          </div>
          <Verdict ok={ok} rationale={rationale} />
        </>
      )}
    </InteractionShell>
  );
}

function choiceBg(correct, wrong, on, cyanAlpha, okA = "0.12", badA = "0.10") {
  if (correct) return `rgba(16,185,129,${okA})`;
  if (wrong) return `rgba(244,63,94,${badA})`;
  if (on) return `${CYAN}${cyanAlpha}`;
  return "#fff";
}

function choiceBorder(correct, wrong, on) {
  if (correct) return "#10B981";
  if (wrong) return "#F43F5E";
  if (on) return CYAN;
  return `${NAVY}20`;
}
